/**
 * Floating action group: scroll-to-top + create-post buttons.
 * Mobile: position:fixed above bottom nav, near right edge.
 * Desktop: position:absolute inside the feed column, bottom-right.
 */
import { Plus, ArrowUp } from 'lucide-react'
import { useAuthStore } from '@/store/auth.store'
import { usePostForm } from '@/context/PostFormContext'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { useFeedStore } from '@/store/feed.store'
import { useActiveGroupStore } from '@/store/activeGroup.store'
import { useIsDesktop } from '@/hooks'
import type { PostFormTab } from '@/context/PostFormContext'

function useTabFromFeed(): PostFormTab {
  const { selectedTypes } = useFeedStore()
  if (selectedTypes.includes('event')) return 'event'
  if (selectedTypes.includes('offer')) return 'bazaar'
  return 'post'
}

function scrollFeedToTop() {
  document.getElementById('feed-scroll')?.scrollTo({ top: 0, behavior: 'smooth' })
}

const fabBase: React.CSSProperties = {
  width: 'var(--fab-size)',
  height: 'var(--fab-size)',
  borderRadius: '50%',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'transform var(--dur-fast) var(--ease-out)',
  flexShrink: 0,
}

const press = (e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.transform = 'scale(0.94)' }
const release = (e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.transform = 'scale(1)' }

export function FloatingPostButton() {
  const { isAuthenticated } = useAuthStore()
  const { openNew } = usePostForm()
  const { t } = useTranslation()
  const tab = useTabFromFeed()
  const isDesktop = useIsDesktop()
  const activeGroup = useActiveGroupStore((s) => s.activeGroup)

  const location = useLocation()

  if (!isAuthenticated) return null
  if (location.pathname.startsWith('/messages')) return null
  // Search's results map (spec-search-map-merge.md) is an embedded,
  // interactive Leaflet surface that can occupy the same bottom-right
  // corner as this FAB — hidden on the whole /search screen rather than
  // only while the map happens to be expanded, since a FAB reappearing
  // mid-scroll as facets toggle would be jarring.
  if (location.pathname === '/search') return null

  const groupStyle: React.CSSProperties = isDesktop
    ? {
        position: 'absolute',
        insetInlineEnd: 16,
        bottom: 24,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        zIndex: 40,
      }
    : {
        position: 'fixed',
        insetInlineEnd: 18,
        bottom: 'calc(var(--bottom-nav) + env(safe-area-inset-bottom, 0px) + 16px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        zIndex: 40,
      }

  return (
    <div style={groupStyle}>
      {/* Scroll to top */}
      <button
        aria-label="Scroll to top"
        onClick={scrollFeedToTop}
        style={{
          ...fabBase,
          background: 'var(--surface)',
          boxShadow: 'var(--shadow-card)',
          color: 'var(--text-muted)',
          border: '1px solid var(--border)',
        }}
        onMouseDown={press}
        onMouseUp={release}
        onMouseLeave={release}
      >
        <ArrowUp size={22} />
      </button>

      {/* Create post */}
      <button
        aria-label={t('post.new_post')}
        onClick={() => openNew(tab, activeGroup ?? undefined)}
        style={{
          ...fabBase,
          background: 'var(--brand)',
          color: '#fff',
          boxShadow: 'var(--shadow-fab)',
        }}
        onMouseDown={press}
        onMouseUp={release}
        onMouseLeave={release}
      >
        <Plus size={26} />
      </button>
    </div>
  )
}
